/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import {
  type ContentStackStorePageType,
  type FormattedContentStackStorePage,
} from '@/features/contentstack/interface/models';
import { getContentStackStoreContent } from '@/features/contentstack/lib/store/getContentStackStoreContent.server'; // eslint-disable-line no-restricted-imports
import { getAxiosForClient } from '@/lib/axios';
import type { PageContextType } from '@/next-types';
import { type QueryClient, useQuery, type QueryFunctionContext } from '@tanstack/react-query';
import { useLocationsParams } from '../hooks/useLocationsParams';

export type GetStoreContentOptions = {
  pageType: ContentStackStorePageType;
  state: string | undefined;
  city: string | undefined;
};

const URL = `/api/contentstack/store`;

const getContentStackStore = async ({
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof getCMSStoreContentKey>
>): Promise<FormattedContentStackStorePage> => {
  const [, , { pageType, state, city }] = queryKey;

  const params: Record<string, string> = {
    pageType,
  };

  if (state) {
    params.state = state;
  }
  if (city) {
    params.city = city;
  }

  const response = await getAxiosForClient().get<FormattedContentStackStorePage>(URL, {
    params,
  });
  return response.data;
};

const getCMSStoreContentKey = ({ pageType, state, city }: GetStoreContentOptions) =>
  [
    'cms',
    'store',
    {
      pageType,
      state,
      city,
    },
  ] as const;

export const useCMSStoreContent = ({
  pageType,
  enabled = true,
  placeholderData,
}: Omit<GetStoreContentOptions, 'state' | 'city'> & {
  enabled?: boolean;
  placeholderData?: FormattedContentStackStorePage;
}) => {
  const { state, city } = useLocationsParams();

  return useQuery({
    queryKey: getCMSStoreContentKey({ pageType, state, city }),
    queryFn: getContentStackStore,
    enabled: enabled && !!pageType,
    placeholderData,
  });
};

export const prefetchCMSStoreContent = async (
  options: GetStoreContentOptions,
  queryClient: QueryClient,
  context: PageContextType,
) => {
  return queryClient.prefetchQuery({
    queryKey: getCMSStoreContentKey(options),
    queryFn: ({ queryKey: [_, __, queryOptions] }) =>
      getContentStackStoreContent(context.req, queryOptions),
  });
};
